import sparkMD5 from 'spark-md5';
import { convertImageToWebp, isHttpUri, isMd5Uri, isSupportWebp, loadImageAsync } from './file-kit';
import { uploadBase64ToObs } from './upload-kit';

export const loadImageAsBase64 = (imageUrl, layerWidth?) => {
    // 最小宽度作为可选值，取img.width 和layerWidth
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            // 优化放到svg中的纹理图大小
            canvas.width = layerWidth ? Math.min(img.width, layerWidth) : img.width;
            canvas.height = Math.floor((canvas.width * img.height) / img.width);
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0, img.width, img.height, 0, 0, canvas.width, canvas.height);
            const dataURL = canvas.toDataURL('image/png');
            resolve(dataURL);
        };
        img.onerror = (err) => {
            console.error('Error loading image:', err);
            resolve('');
            reject(err);
        };
        img.src = imageUrl;
    });
};

// md5 -> base64 的内存映射
const mapping: Map<string, string> = new Map();
/**
 * 上传 Base64 到远程 —— Base64 写入 indexdb 后删除内存映射
 *
 * @param md5 - MD5 编码
 * @param base64 - Base64 编码
 */
const uploadAsync = async (md5: string, base64: string) => {
    const url = await base64Kit.uploadImgAsync(md5, base64);
    if (url) {
        await loadImageAsync(url);
        mapping.set(md5, url);
    }
};

/**
 * Base64 图片管理
 */
export const base64Kit = {
    setRemoteUrl(code: string, url: string) {
        if (isHttpUri(url)) {
            mapping.set(code, url);
        }
    },

    /**
     * 获取远程地址
     *
     * @param code - 代码
     */
    getRemoteUrl(code: string) {
        if (isHttpUri(code)) {
            return code;
        }
        const data = mapping.get(code) || '';
        return isHttpUri(data) ? data : '';
    },
    /**
     * 异步获取远程地址
     *
     * @param code - 代码
     * @param timer - 可选；定时器时间，单位：毫秒；默认值为 `500`
     */
    getRemoteUrlAsync(code: string, timer = 500): Promise<string> {
        return new Promise((resolve) => {
            if (isHttpUri(code)) {
                resolve(code);
            } else {
                const getUrl = (code: string) => {
                    const url = base64Kit.getRemoteUrl(code);
                    if (url) {
                        resolve(url);
                    } else {
                        if (mapping.get(code)) {
                            setTimeout(() => {
                                getUrl(code);
                            }, timer);
                        } else {
                            resolve('');
                        }
                    }
                };
                getUrl(code);
            }
        });
    },
    /**
     * 下载、保存，重新调用上传图片
     *
     * @param code - 代码
     * @param base64 - Base64 编码
     */
    async uploadImgAsync(code: string, base64: string) {
        if (!code) return code;
        if (isHttpUri(code)) return code;
        if (!isMd5Uri(code)) return code;
        const data = mapping.get(code);
        if (!data) return code;
        if (isHttpUri(data)) return data;
        try {
            // console.time(`上传图片 =>${code}`);
            const res = await uploadBase64ToObs(base64);
            mapping.set(code, res.url);
            // console.timeEnd(`上传图片 =>${code}`);
            return res.url;
        } catch (e) {
            console.log('upload img error => ', e);
        }
    },
    /**
     * 转成 MD5
     *
     * @param item - 元素项
     */
    md5(item: string = ' ') {
        return sparkMD5.hash(item);
    },
    /**
     * 根据文件链接获取 MD5 编码（并同时异步上传文件到 OBS）
     *
     * @param fileUrl - 文件链接
     * @param forceMd5 - 可选；强制使用传入的值作为 MD5 计算
     */
    getCode(fileUrl: string, forceMd5?: string) {
        if (!fileUrl) return fileUrl;
        if (isHttpUri(fileUrl)) {
            return fileUrl;
        }
        if (isMd5Uri(fileUrl)) return fileUrl;
        let md5 = '';
        if (forceMd5) {
            // 使用传入的值作为内容的映射
            md5 = sparkMD5.hash(forceMd5);
        } else {
            md5 = sparkMD5.hash(fileUrl);
        }
        if (mapping.get(md5)) {
            return md5;
        }
        mapping.set(md5, fileUrl);
        uploadAsync(md5, fileUrl);
        return md5;
    },
    getLocalUrl(code: string) {
        if (isHttpUri(code)) {
            return code;
        }
        const url = mapping.get(code) || '';
        if (isSupportWebp() && url) {
            return convertImageToWebp(url);
        }
        return url || code;
    },

    /**
     * 直接保存为 Base64 编码
     *
     * @param md5 - MD5 编码
     * @param base64 - Base64 编码
     */
    save(md5: string, base64: string): boolean {
        mapping.set(md5, base64);
        return true;
    },

    /**
     * 是否已存在
     *
     * @param code - 代码
     */
    hasCode(code: string) {
        const data = mapping.get(code);
        return !!data;
    },
};
